import React, { forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ArrowCounterClockwiseIcon,
  ArrowUUpLeftIcon,
} from '@phosphor-icons/react';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { SpreadsheetViewers } from '../index';
import FieldMappingTableWithData from '../FieldMappingTableWithData';
import WorkflowConstantsPanel from '../WorkflowConstantsPanel';
import type { WorkflowConstants } from '../../types/WorkflowConstants';
import {
  FieldMappings,
  MappingValue,
} from 'src/api/spreadsheet/workflow/useWorkflowMapFields';
import useDivisions from 'src/api/organization/useDivisions';
import { useUpsertAliases } from 'src/api/core/aliases/useAliases';
import { useProductGroupsBySupplier } from 'src/api/rebates/useProductGroups';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import SimpleDataTable, {
  SimpleDataTableColumn,
} from 'src/components/common/SimpleDataTable';
import { Checkbox } from '@/components/ui/checkbox';
import config from 'src/config';

// Helper to normalize mapping value to string
function normalizeMappingValueToString(
  input: string | MappingValue | undefined,
): string {
  if (!input) return '';
  if (typeof input === 'string') return input;
  if (input.type === 'simple') return input.value;
  // For split mappings, we can't use a single string, so return empty
  return '';
}

// Separator used to replace '|||' delimiter in alias display
const ALIAS_SEPARATOR = ' • ';

/**
 * Phase 2: Field Mapping & Constants Extraction
 *
 * Purpose: Extract constants and create field mappings to transform preprocessed data
 * Input: Clean preprocessed data from Phase 1
 * Output: Mapped data ready for workflow generation in Phase 3
 */

interface PhaseTwoMappingProps {
  // Essential data only
  preprocessedData: unknown[][];
  mappedData: unknown[][];

  // Local state for editing
  constants: WorkflowConstants;
  supplierName?: string;
  onConstantsChange?: (constants: WorkflowConstants) => void;
  mappings: FieldMappings;

  onMappingsChange: React.Dispatch<React.SetStateAction<FieldMappings>>;

  // Action handlers
  onApplyMappings: () => void; // Apply current mappings to data
  onResetMappings: () => void; // Reset mappings to last applied state

  // UI state
  isLoading: boolean;
  hasUnappliedChanges: boolean;
  error: string | null;
  filename: string;
}

// Ref handle exposed to parent for imperative calls
export interface PhaseTwoMappingHandle {
  /**
   * Check for new aliases and show confirmation dialog before continuing.
   * Call this before transitioning from Phase 2 to Phase 3.
   * @param onContinue - callback to execute after alias check/save completes
   */
  checkAliasesBeforeContinue: (onContinue: () => void) => void;
}

const PhaseTwoMapping = forwardRef<PhaseTwoMappingHandle, PhaseTwoMappingProps>(
  function PhaseTwoMapping(
    {
      preprocessedData,
      mappedData,
      constants,
      supplierName,
      onConstantsChange,
      mappings,
      onMappingsChange,
      onApplyMappings,
      onResetMappings,
      isLoading,
      hasUnappliedChanges,
      error,
      filename,
    },
    ref,
  ) {
    const { mutateAsync: upsertAliases } = useUpsertAliases();
    const { data: divisionsData } = useDivisions(true);
    const divisionNameToId = React.useMemo(() => {
      const map = new Map<string, string>();
      divisionsData?.divisions?.forEach((d) => {
        if (d.name) map.set(d.name, d.id);
      });
      return map;
    }, [divisionsData]);

    // Fetch product groups for the current supplier
    const supplierId = constants.supplierId as string | undefined;
    const { data: productGroups } = useProductGroupsBySupplier(
      supplierId || '',
      !!supplierId,
    );
    const productGroupNameToId = React.useMemo(() => {
      const map = new Map<string, string>();
      productGroups?.forEach((pg) => {
        if (pg.name) map.set(pg.name, pg.id);
      });
      return map;
    }, [productGroups]);

    // Keep a stable ref to mappings to avoid handleApplyClick identity changes
    const latestMappingsRef = React.useRef(mappings);
    React.useEffect(() => {
      latestMappingsRef.current = mappings;
    }, [mappings]);

    // Stable click handler that always invokes the latest onApplyMappings
    const latestApplyRef = React.useRef(onApplyMappings);
    React.useEffect(() => {
      latestApplyRef.current = onApplyMappings;
    }, [onApplyMappings]);
    const hasConstants = Object.keys(constants).length > 0;
    const hasMappings = Object.values(mappings).some(
      (mapping) => Object.keys(mapping.mappings || {}).length > 0,
    );

    const hasAnyData = hasConstants || hasMappings;

    // Build sections first, then assemble
    const header = (
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-4">Field Mapping & Constants</h1>
        <p className="text-base text-gray-600">
          The goal of this step is to figure out how to map divisions, products,
          etc to values in our system. Review and edit the AI-extracted
          constants and field mappings. These will be used to transform your
          data for the final workflow.
        </p>
      </div>
    );

    const loadingCard =
      !hasAnyData && isLoading ? (
        <Card className="mb-8">
          <CardContent>
            <LoadingSpinner
              text="Analyzing data and extracting field mappings..."
              minHeight={300}
              size={60}
            />
          </CardContent>
        </Card>
      ) : null;

    const emptyCard =
      !hasAnyData && !isLoading ? (
        <Card className="mb-8">
          <CardContent>
            <h3 className="text-lg font-medium text-center text-gray-600">
              No constants or field mappings found
            </h3>
            <p className="text-sm text-center text-gray-600 mt-2">
              The AI analysis didn&apos;t extract any constants or field
              mappings from your data.
            </p>
          </CardContent>
        </Card>
      ) : null;

    const constantsCard = hasConstants ? (
      <WorkflowConstantsPanel
        constants={constants}
        supplierName={supplierName}
        phase={2}
        onConstantsChange={onConstantsChange}
      />
    ) : null;

    const mappingsCard = hasMappings ? (
      <Card className="mb-8">
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Field Mappings</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={onResetMappings}
                    disabled={!hasUnappliedChanges || isLoading}
                    size="sm"
                    className="text-gray-700 border-gray-300 hover:bg-gray-50"
                  >
                    <ArrowUUpLeftIcon className="w-4 h-4 mr-2" />
                    Reset Changes
                  </Button>
                </span>
              </TooltipTrigger>
              {(!hasUnappliedChanges || isLoading) && (
                <TooltipContent>
                  <p>
                    {isLoading
                      ? 'Cannot reset while applying changes'
                      : 'No unapplied changes to reset'}
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
          <FieldMappingTableWithData
            mappings={mappings}
            setMappings={onMappingsChange}
            constants={constants}
            disabled={isLoading}
          />
        </CardContent>
      </Card>
    ) : null;

    const errorCard = error ? (
      <Card className="mb-8 border-red-500">
        <CardContent>
          <p className="text-sm text-red-600">{error}</p>
        </CardContent>
      </Card>
    ) : null;

    // Warning banner for unapplied changes
    const unappliedChangesWarning = hasUnappliedChanges ? (
      <Card className="mb-8 border-orange-400 bg-orange-50">
        <CardContent className="py-3">
          <div className="flex items-center gap-2">
            <span className="text-orange-600 text-xl">⚠️</span>
            <p className="text-sm text-orange-800 font-medium">
              You have unapplied changes. Click &quot;Apply Changes&quot; to
              update the preview data before continuing.
            </p>
          </div>
        </CardContent>
      </Card>
    ) : null;

    // Ensure handler is declared before memo that consumes it
    const handleApplyButton = React.useCallback(() => {
      applyClickRef.current();
    }, []);

    const dataPreview = React.useMemo(() => {
      const actionButtons = (
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            onClick={handleApplyButton}
            disabled={isLoading}
            className={`${
              hasUnappliedChanges
                ? 'bg-orange-600 hover:bg-orange-700 animate-pulse'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white transition-all`}
          >
            <ArrowCounterClockwiseIcon className="w-4 h-4 mr-2" />
            {isLoading ? 'Applying...' : 'Apply Changes'}
          </Button>
          {hasUnappliedChanges && !isLoading && (
            <Badge
              variant="outline"
              className="bg-orange-100 text-orange-700 border-orange-400"
            >
              Unsaved
            </Badge>
          )}
        </div>
      );

      return (
        <SpreadsheetViewers
          title="Data Preview"
          headerChildren={actionButtons}
          originalTitle="Preprocessed Data"
          transformedTitle="Mapped Data"
          originalData={preprocessedData}
          transformedData={mappedData}
          isLoadingOriginal={false}
          isLoadingTransformed={isLoading}
          transformError={null}
          workbook={null}
          selectedSheet={undefined}
          onOriginalDataChange={undefined}
          filename={filename}
        />
      );
    }, [
      preprocessedData,
      mappedData,
      isLoading,
      hasUnappliedChanges,
      filename,
      handleApplyButton,
    ]);

    // Confirm dialog state for saving aliases
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    // Division alias types
    type PendingDivisionAliasRow = {
      alias: string;
      divisionName: string;
      divisionId: string;
    };
    const [pendingDivisionAliases, setPendingDivisionAliases] = React.useState<
      PendingDivisionAliasRow[]
    >([]);

    // Product group alias types
    type PendingProductGroupAliasRow = {
      alias: string;
      productGroupName: string;
      productGroupId: string;
    };
    const [pendingProductGroupAliases, setPendingProductGroupAliases] =
      React.useState<PendingProductGroupAliasRow[]>([]);

    // Unified selection tracking for both types
    const [selectedAliasKeys, setSelectedAliasKeys] = React.useState<
      Set<string>
    >(new Set());

    const divisionAliasKey = React.useCallback(
      (row: PendingDivisionAliasRow) =>
        `division:${row.divisionId}:${row.alias}`,
      [],
    );
    const productGroupAliasKey = React.useCallback(
      (row: PendingProductGroupAliasRow) =>
        `product_group:${row.productGroupId}:${row.alias}`,
      [],
    );

    const normalize = (s: string) =>
      s.toLowerCase().trim().replace(/\s+/g, ' ');

    // Ref to store the callback to execute after alias dialog completes
    const afterAliasDialogRef = React.useRef<(() => void) | null>(null);

    /**
     * Check for new aliases and show confirmation dialog if needed.
     * @param afterComplete - optional callback to execute after the alias check/save completes.
     *                        If not provided, defaults to calling onApplyMappings.
     */
    const openConfirmIfNeeded = React.useCallback(
      async (afterComplete?: () => void) => {
        // Store the callback to be called after dialog completes
        afterAliasDialogRef.current = afterComplete ?? null;
        // --- Division aliases ---
        const divisionMapping =
          latestMappingsRef.current?.divisionMappings?.mappings || {};
        const rawDivisionAliases = Array.from(
          new Set(
            Object.keys(divisionMapping)
              .map((k) => k?.toString?.() ?? '')
              .filter((k) => k && k !== '*'),
          ),
        );

        const divisionRows: PendingDivisionAliasRow[] = [];
        for (const alias of rawDivisionAliases) {
          const divisionMappingValue = divisionMapping[alias];
          const divisionName =
            normalizeMappingValueToString(divisionMappingValue);
          if (!divisionName || divisionName === 'UNKNOWN') continue;
          // Skip if the alias is a direct string match to the division name (no alias needed)
          if (normalize(alias) === normalize(divisionName)) continue;
          const divisionId = divisionNameToId.get(divisionName);
          if (!divisionId) continue;
          divisionRows.push({ alias, divisionName, divisionId });
        }

        // --- Product group aliases ---
        const productMapping =
          latestMappingsRef.current?.productMappings?.mappings || {};
        const rawProductAliases = Array.from(
          new Set(
            Object.keys(productMapping)
              .map((k) => k?.toString?.() ?? '')
              .filter((k) => k && k !== '*'),
          ),
        );

        const productRows: PendingProductGroupAliasRow[] = [];
        for (const alias of rawProductAliases) {
          const productMappingValue = productMapping[alias];
          const productGroupName =
            normalizeMappingValueToString(productMappingValue);
          if (!productGroupName || productGroupName === 'UNKNOWN') continue;
          // Skip if the alias is a direct string match to the product group name (no alias needed)
          if (normalize(alias) === normalize(productGroupName)) continue;
          const productGroupId = productGroupNameToId.get(productGroupName);
          if (!productGroupId) continue;
          productRows.push({ alias, productGroupName, productGroupId });
        }

        // If no aliases to potentially save, call the completion callback
        if (divisionRows.length === 0 && productRows.length === 0) {
          if (afterAliasDialogRef.current) {
            afterAliasDialogRef.current();
            afterAliasDialogRef.current = null;
          } else {
            latestApplyRef.current?.();
          }
          return;
        }

        // --- Fetch existing division aliases ---
        const byDivision = new Map<string, string[]>();
        divisionRows.forEach((r) => {
          const list = byDivision.get(r.divisionId) ?? [];
          list.push(r.alias);
          byDivision.set(r.divisionId, list);
        });

        const existingDivisionSets = new Map<string, Set<string>>();
        await Promise.all(
          Array.from(byDivision.keys()).map(async (divisionId) => {
            const res = await fetch(
              `${config.apiBaseUrl}/organization/divisions/${divisionId}/aliases`,
              { credentials: 'include' },
            );
            if (!res.ok) {
              existingDivisionSets.set(divisionId, new Set());
              return;
            }
            const json = await res.json();
            const aliases: string[] = (json?.data?.aliases || []).map(
              (a: { alias: string }) => a.alias,
            );
            existingDivisionSets.set(
              divisionId,
              new Set(aliases.map(normalize)),
            );
          }),
        );

        // --- Fetch existing product group aliases ---
        const byProductGroup = new Map<string, string[]>();
        productRows.forEach((r) => {
          const list = byProductGroup.get(r.productGroupId) ?? [];
          list.push(r.alias);
          byProductGroup.set(r.productGroupId, list);
        });

        const existingProductGroupSets = new Map<string, Set<string>>();
        await Promise.all(
          Array.from(byProductGroup.keys()).map(async (productGroupId) => {
            const res = await fetch(
              `${config.apiBaseUrl}/rebates/product-groups/${productGroupId}/aliases`,
              { credentials: 'include' },
            );
            if (!res.ok) {
              existingProductGroupSets.set(productGroupId, new Set());
              return;
            }
            const json = await res.json();
            const aliases: string[] = (json?.data?.aliases || []).map(
              (a: { alias: string }) => a.alias,
            );
            existingProductGroupSets.set(
              productGroupId,
              new Set(aliases.map(normalize)),
            );
          }),
        );

        // Filter to only new aliases
        const newDivisionAliases = divisionRows.filter(
          (r) =>
            !existingDivisionSets.get(r.divisionId)?.has(normalize(r.alias)),
        );
        const newProductGroupAliases = productRows.filter(
          (r) =>
            !existingProductGroupSets
              .get(r.productGroupId)
              ?.has(normalize(r.alias)),
        );

        if (
          newDivisionAliases.length > 0 ||
          newProductGroupAliases.length > 0
        ) {
          setPendingDivisionAliases(newDivisionAliases);
          setPendingProductGroupAliases(newProductGroupAliases);
          // Default all to selected
          const allKeys = new Set([
            ...newDivisionAliases.map(
              (r) => `division:${r.divisionId}:${r.alias}`,
            ),
            ...newProductGroupAliases.map(
              (r) => `product_group:${r.productGroupId}:${r.alias}`,
            ),
          ]);
          setSelectedAliasKeys(allKeys);
          setConfirmOpen(true);
        } else {
          // Nothing new to save, call completion callback
          if (afterAliasDialogRef.current) {
            afterAliasDialogRef.current();
            afterAliasDialogRef.current = null;
          } else {
            latestApplyRef.current?.();
          }
        }
      },
      [divisionNameToId, productGroupNameToId],
    );

    // Expose imperative handle for parent to call before phase transition
    useImperativeHandle(
      ref,
      () => ({
        checkAliasesBeforeContinue: (onContinue: () => void) => {
          void openConfirmIfNeeded(onContinue);
        },
      }),
      [openConfirmIfNeeded],
    );

    // Replace click to open dialog flow
    const onApplyClickWithDialog = React.useCallback(() => {
      openConfirmIfNeeded();
    }, [openConfirmIfNeeded]);

    // Swap in the new click handler
    const applyClickRef = React.useRef(onApplyClickWithDialog);
    React.useEffect(() => {
      applyClickRef.current = onApplyClickWithDialog;
    }, [onApplyClickWithDialog]);

    // Overwrite earlier button handler binding (moved above for linter)

    // Render confirm dialog content listing unique aliases
    const [confirmLoading, setConfirmLoading] = React.useState(false);

    // Define columns for division alias confirmation table
    const divisionAliasTableColumns: SimpleDataTableColumn<PendingDivisionAliasRow>[] =
      [
        {
          key: 'include',
          header: 'Include',
          width: 'w-20',
          render: (row) => (
            <Checkbox
              checked={selectedAliasKeys.has(divisionAliasKey(row))}
              onCheckedChange={(checked) =>
                setSelectedAliasKeys((prev) => {
                  const next = new Set(prev);
                  const key = divisionAliasKey(row);
                  if (checked) next.add(key);
                  else next.delete(key);
                  return next;
                })
              }
            />
          ),
        },
        {
          key: 'division',
          header: 'Division',
          className: 'truncate max-w-32',
          render: (row) => row.divisionName,
        },
        {
          key: 'alias',
          header: 'New Alias',
          className: 'truncate max-w-48',
          render: (row) => row.alias.replaceAll('|||', ALIAS_SEPARATOR),
        },
      ];

    // Define columns for product group alias confirmation table
    const productGroupAliasTableColumns: SimpleDataTableColumn<PendingProductGroupAliasRow>[] =
      [
        {
          key: 'include',
          header: 'Include',
          width: 'w-20',
          render: (row) => (
            <Checkbox
              checked={selectedAliasKeys.has(productGroupAliasKey(row))}
              onCheckedChange={(checked) =>
                setSelectedAliasKeys((prev) => {
                  const next = new Set(prev);
                  const key = productGroupAliasKey(row);
                  if (checked) next.add(key);
                  else next.delete(key);
                  return next;
                })
              }
            />
          ),
        },
        {
          key: 'productGroup',
          header: 'Product Group',
          className: 'truncate max-w-32',
          render: (row) => row.productGroupName,
        },
        {
          key: 'alias',
          header: 'New Alias',
          className: 'truncate max-w-48',
          render: (row) => row.alias.replaceAll('|||', ALIAS_SEPARATOR),
        },
      ];

    // Select all/deselect all functionality
    const allDivisionAliasKeys = pendingDivisionAliases.map(divisionAliasKey);
    const allProductGroupAliasKeys =
      pendingProductGroupAliases.map(productGroupAliasKey);
    const allAliasKeys = [...allDivisionAliasKeys, ...allProductGroupAliasKeys];
    const allSelected =
      allAliasKeys.length > 0 &&
      allAliasKeys.every((key) => selectedAliasKeys.has(key));
    const someSelected = allAliasKeys.some((key) => selectedAliasKeys.has(key));

    const handleSelectAll = () => {
      if (allSelected) {
        // Deselect all
        setSelectedAliasKeys(new Set());
      } else {
        // Select all
        setSelectedAliasKeys(new Set(allAliasKeys));
      }
    };

    const confirmDialog = (
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        wide={true}
        onConfirm={async () => {
          try {
            setConfirmLoading(true);

            // Build divisionId -> aliases[] batches from selected rows
            const divisionBatches = new Map<string, string[]>();
            for (const row of pendingDivisionAliases) {
              if (!selectedAliasKeys.has(divisionAliasKey(row))) continue;
              const list = divisionBatches.get(row.divisionId) ?? [];
              list.push(row.alias);
              divisionBatches.set(row.divisionId, list);
            }

            // Build productGroupId -> aliases[] batches from selected rows
            const productGroupBatches = new Map<string, string[]>();
            for (const row of pendingProductGroupAliases) {
              if (!selectedAliasKeys.has(productGroupAliasKey(row))) continue;
              const list = productGroupBatches.get(row.productGroupId) ?? [];
              list.push(row.alias);
              productGroupBatches.set(row.productGroupId, list);
            }

            // Save both in parallel using generic alias API
            const divisionOps = Array.from(divisionBatches.entries()).map(
              ([divisionId, aliases]) =>
                upsertAliases({
                  entityType: 'division',
                  entityId: divisionId,
                  aliases,
                }),
            );
            const productGroupOps = Array.from(
              productGroupBatches.entries(),
            ).map(([productGroupId, aliases]) =>
              upsertAliases({
                entityType: 'product_group',
                entityId: productGroupId,
                aliases,
              }),
            );

            const allOps = [...divisionOps, ...productGroupOps];
            if (allOps.length > 0) await Promise.all(allOps);
          } finally {
            setConfirmLoading(false);
            setConfirmOpen(false);
            // Call the stored callback if present, otherwise default to apply
            if (afterAliasDialogRef.current) {
              afterAliasDialogRef.current();
              afterAliasDialogRef.current = null;
            } else {
              latestApplyRef.current?.();
            }
          }
        }}
        title="Save new alias mappings?"
        confirmText={`Save ${selectedAliasKeys.size} & Apply`}
        loading={confirmLoading}
      >
        <div className="space-y-4">
          {/* Select All/Deselect All Controls */}
          <div className="flex items-center justify-between border-b pb-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    const input = el.querySelector(
                      'input[type="checkbox"]',
                    ) as HTMLInputElement;
                    if (input) {
                      input.indeterminate = someSelected && !allSelected;
                    }
                  }
                }}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm font-medium">
                {allSelected ? 'Deselect All' : 'Select All'}
              </span>
            </div>
            <span className="text-sm text-muted-foreground">
              {selectedAliasKeys.size} of{' '}
              {pendingDivisionAliases.length +
                pendingProductGroupAliases.length}{' '}
              selected
            </span>
          </div>

          {/* Division Aliases Section */}
          {pendingDivisionAliases.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">
                Division Aliases ({pendingDivisionAliases.length} new)
              </h4>
              <SimpleDataTable
                data={pendingDivisionAliases}
                columns={divisionAliasTableColumns}
                getRowKey={(row) => `division:${row.divisionId}:${row.alias}`}
                className="text-sm"
                containerClassName="max-h-48 overflow-y-auto"
              />
            </div>
          )}

          {/* Product Group Aliases Section */}
          {pendingProductGroupAliases.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">
                Product Group Aliases ({pendingProductGroupAliases.length} new)
              </h4>
              <SimpleDataTable
                data={pendingProductGroupAliases}
                columns={productGroupAliasTableColumns}
                getRowKey={(row) =>
                  `product_group:${row.productGroupId}:${row.alias}`
                }
                className="text-sm"
                containerClassName="max-h-48 overflow-y-auto"
              />
            </div>
          )}
        </div>
      </ConfirmDialog>
    );

    return (
      <div>
        <div className="max-w-6xl mx-auto">
          {header}
          {loadingCard}
          {emptyCard}
          {constantsCard}
          {mappingsCard}
          {errorCard}
          {unappliedChangesWarning}
        </div>
        {preprocessedData.length > 0 ? dataPreview : null}
        {confirmDialog}
      </div>
    );
  },
);

export default PhaseTwoMapping;
